import React, { FC, Fragment, useEffect, useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import InvoiceItem from '../invoice-item/invoice-item.component';
import { useTranslation } from 'react-i18next';
import { ApolloError, DocumentNode, NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Invoice, InvoiceFilter, InvoiceItemContextEvent, InvoiceLineItem } from 'models/invoice';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { useNavigate } from 'react-router-dom';
import InvoiceItemContextMenu from '../invoice-item/invoice-item-context-menu.component';
import SendMailDialogComponent from '../send-mail-dialog/send-mail-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { OrderBy, OrderDirection } from 'models/search-result';
import SetInvoiceAsPaidComponent from 'components/invoice-summary/set-invoice-as-paid-dialog/set-invoice-as-paid-dialog.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { flagInvoiceAsUnpaidTemplate } from 'apollo/templates/invoices';
import SetSentDateComponent from '../set-send-date-dialog/set-send-date-dialog.component';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { Order } from 'models/order';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type InvoicesListProps = {
    invoiceFilter: InvoiceFilter;
    queryTemplate: DocumentNode;
    queryName: string;
};

const InvoicesList: FC<InvoicesListProps> = ({ invoiceFilter, queryTemplate, queryName }: InvoicesListProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const [lastOffset, setLastOffset] = useState(0);
    const [open, setOpen] = useState(false);
    const [isPaidDateDialogOpen, setIsPaidDateDialogOpen] = useState(false);
    const [isSentDateDialogOpen, setIsSentDateDialogOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isMailSent, setIsMailSent] = useState(false);
    const [invoicePaidSnackBar, setInvoicePaidSnackBar] = useState(false);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'invoiceDate',
        orderDirection: OrderDirection.desc,
    });
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [apiError, setApiError] = useState<ApolloError | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoiceContextMenu, setInvoiceContextMenu] = useState<InvoiceItemContextEvent | null>(null);

    const currentUser = currentUserVar();
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );

    const getFormattedFilters = (invoiceFilter: InvoiceFilter) => {
        const filters = { myInvoice: permissions.isDentist(currentUser.type) ? true : false, ...invoiceFilter };
        delete filters.clinic;
        delete filters.lab;
        return filters;
    };

    const updateInvoice = (invoice: Invoice) => {
        const invoiceArr = [...invoices];
        const index = invoiceArr.findIndex((inv) => inv._id === invoice?._id);
        if (index > -1) {
            invoiceArr.splice(index, 1, Object.assign({}, { ...invoiceArr[index] }, invoice));
            setInvoices(invoiceArr);
        }
    };

    const [getInvoices, { loading, fetchMore, networkStatus }] = useLazyQuery(queryTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
        onCompleted: (data) => {
            const invoices: Invoice[] = data[queryName] || [];
            setInvoices(
                invoices.map((invoice: Invoice) => {
                    const itemWithOrder = invoice?.lineItems?.filter((item: InvoiceLineItem) => !!item.order) || [];
                    const certifiedOrders = itemWithOrder.filter(
                        (item: InvoiceLineItem) => (item.order as Order)?.isCertified,
                    );
                    return {
                        ...invoice,
                        lineItemWithOrderCount: itemWithOrder.length,
                        certifiedOrderCount: certifiedOrders.length,
                    };
                }),
            );
            setLastOffset(invoices?.length);
        },
    });

    const [flagInvoiceAsUnpaid] = useMutation(flagInvoiceAsUnpaidTemplate, {
        onCompleted({ flagInvoiceAsUnpaid: unpaidInvoice }) {
            updateInvoice(unpaidInvoice as Invoice);
            setIsConfirmDialogOpen(false);
            setInvoice(null);
        },
        onError: (err: ApolloError) => {
            setApiError(err);
            setIsConfirmDialogOpen(false);
            setInvoice(null);
        },
    });

    useEffect(() => {
        getInvoices({
            variables: {
                filters: getFormattedFilters(invoiceFilter),
                limit: 20,
                offset: 0,
                ...orderBy,
            },
        });
    }, [invoiceFilter]);

    const handleInvoiceClick = (invoiceId: string) => {
        navigate(`/invoices/${invoiceId}/summary`);
    };

    const handleFetchMore = () => {
        if (invoiceFilter.myInvoice === false || permissions.isLabTechnician(currentUser.type)) {
            if (fetchMore) {
                fetchMore({
                    variables: {
                        offset: lastOffset,
                    },
                });
            } else {
                getInvoices({
                    variables: {
                        filters: getFormattedFilters(invoiceFilter),
                        limit: 20,
                        offset: lastOffset,
                        ...orderBy,
                    },
                });
            }
        }
    };

    const handleContextMenu = (event: InvoiceItemContextEvent) => {
        setInvoiceContextMenu(invoiceContextMenu === null ? event : null);
    };

    const handleCloseContextMenu = () => {
        setInvoiceContextMenu(null);
    };

    const openSendInvoiceDialog = (invoice: Invoice) => {
        setOpen(true);
        setInvoice(invoice);
        handleCloseContextMenu();
    };

    const handleDialogClose = () => {
        setOpen(false);
        setInvoice(null);
    };

    const handleSendMail = (error?: ApolloError, invoice?: Invoice) => {
        if (error) {
            setApiError(error);
        } else {
            updateInvoice(invoice as Invoice);
            setIsMailSent(true);
        }
        setOpen(false);
        setInvoice(null);
    };

    const setOrderOnField = async (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        await getInvoices({
            variables: {
                filters: getFormattedFilters(invoiceFilter),
                limit: 20,
                offset: 0,
                orderBy: field,
                orderDirection: direction,
            },
        });
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
    };

    const setInvoiceAndCloseMenu = () => {
        setInvoice(invoiceContextMenu?.invoice as Invoice);
        handleCloseContextMenu();
    };

    const openPaidDateDialog = () => {
        setIsPaidDateDialogOpen(true);
        setInvoiceAndCloseMenu();
    };

    const openSetSendDateDialog = () => {
        setIsSentDateDialogOpen(true);
        setInvoiceAndCloseMenu();
    };

    const openSetUnpaidDialog = () => {
        setIsConfirmDialogOpen(true);
        setInvoiceAndCloseMenu();
    };

    const handlePaymentDateSet = (error?: ApolloError, updatedInvoice?: Invoice) => {
        if (error) {
            setApiError(error);
        } else {
            updateInvoice(updatedInvoice as Invoice);
            setInvoicePaidSnackBar(true);
        }
        setIsPaidDateDialogOpen(false);
        setInvoice(null);
    };

    const handleSentDateSet = (error?: ApolloError, updatedInvoice?: Invoice) => {
        if (error) {
            setApiError(error);
        } else {
            updateInvoice(updatedInvoice as Invoice);
            // setInvoicePaidSnackBar(true);
        }
        setIsSentDateDialogOpen(false);
        setInvoice(null);
    };

    const handlePaidDateDialogClose = () => {
        setIsPaidDateDialogOpen(false);
        setInvoice(null);
    };

    const handleSentDateDialogClose = () => {
        setIsSentDateDialogOpen(false);
        setInvoice(null);
    };

    const handleConfirmDialogClose = () => {
        setIsConfirmDialogOpen(false);
        setInvoice(null);
    };

    const onConfirm = async () => {
        // call unpaid API here
        await flagInvoiceAsUnpaid({ variables: { invoiceId: invoice?._id as string } });
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, invoices?.length || 0);

    if ((loading && networkStatus !== NetworkStatus.fetchMore) || !invoices) {
        return <Loader />;
    }

    return (
        <Fragment>
            {apiError ? <AlertSnackbar type="error" message={apiError?.message} /> : undefined}
            {isMailSent ? <AlertSnackbar type="success" message={translate('invoices:MAIL_SENT')} /> : undefined}
            {invoicePaidSnackBar ? (
                <AlertSnackbar type="success" message={translate('invoices:SET_AS_PAID')} />
            ) : undefined}
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={invoices ? invoices.length : 0}
                    next={handleFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                {isCertificationEnabled && permissions.isDentist(currentUser.type) ? (
                                    <TableCell>{translate('orders:CERTIFICATION')}</TableCell>
                                ) : undefined}
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('invoiceDate')}
                                        active={orderBy.orderBy === 'invoiceDate'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('invoices:INVOICE_DATE')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('dueDate')}
                                        active={orderBy.orderBy === 'dueDate'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('invoices:DUE_DATE')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('invoiceNumber')}
                                        active={orderBy.orderBy === 'invoiceNumber'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:INVOICE')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    {permissions.isLabTechnician(currentUser.type)
                                        ? translate('common:CLINIC')
                                        : translate('common:LAB')}
                                </TableCell>
                                <TableCell>{translate('invoices:YOUR_REFERENCE')}</TableCell>
                                <TableCell>{translate('invoices:INVOICE_TYPE')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('sent.on')}
                                        active={orderBy.orderBy === 'sent.on'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:SENT')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((invoice: Invoice, index: number) => (
                                <InvoiceItem
                                    isCertificationEnabled={isCertificationEnabled}
                                    isTechnician={permissions.isLabTechnician(currentUser.type)}
                                    key={index}
                                    onInvoiceClick={handleInvoiceClick}
                                    invoice={invoice}
                                    onInvoiceContextMenu={handleContextMenu}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            {invoiceContextMenu ? (
                <InvoiceItemContextMenu
                    invoiceContextEvent={invoiceContextMenu}
                    onCloseContextMenu={handleCloseContextMenu}
                    openSendInvoiceDialog={openSendInvoiceDialog}
                    openPaidDateModal={openPaidDateDialog}
                    openSetUnpaidModal={openSetUnpaidDialog}
                    openSetSendDateDialog={openSetSendDateDialog}
                />
            ) : undefined}
            <SendMailDialogComponent
                open={open}
                invoice={invoice}
                onDialogClose={handleDialogClose}
                onSendMail={handleSendMail}
            />
            <SetInvoiceAsPaidComponent
                open={isPaidDateDialogOpen}
                invoiceId={invoice?._id as string}
                invoiceSentDate={invoice?.sentDate as Date}
                onDialogClose={handlePaidDateDialogClose}
                onPaymentDateSet={handlePaymentDateSet}
            />
            <SetSentDateComponent
                open={isSentDateDialogOpen}
                invoiceId={invoice?._id as string}
                onDialogClose={handleSentDateDialogClose}
                onSentDateSet={handleSentDateSet}
            />
            <ConfirmDialog
                open={isConfirmDialogOpen}
                handleConfirm={onConfirm}
                handleClose={handleConfirmDialogClose}
                confirmBtnLabel={translate('common:YES')}
            />
        </Fragment>
    );
};

export default InvoicesList;
